import { browserInitialLocale } from '../utils/initialLocale';
import type { PlatformStore } from './types';

const PREFIX = 'vo2_pwa_';

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  localStorage.setItem(PREFIX + key, JSON.stringify(value));
}

function readString(key: string, fallback = ''): string {
  return localStorage.getItem(PREFIX + key) ?? fallback;
}

function writeString(key: string, value: string): void {
  localStorage.setItem(PREFIX + key, value);
}

const defaultApiUrl =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
  'https://api.vo2peaks.com';

/** localStorage-backed store for the web app. */
export const webStorage: PlatformStore = {
  async getToken() {
    return readString('token');
  },
  async setToken(token: string) {
    writeString('token', token);
  },
  async clearToken() {
    localStorage.removeItem(PREFIX + 'token');
    localStorage.removeItem(PREFIX + 'refresh');
  },
  async getUser() {
    return readJson<Record<string, unknown>>('user', {});
  },
  async setUser(user: Record<string, unknown>) {
    writeJson('user', user);
  },
  async getSelectedAthleteId() {
    const v = readJson<number | null>('selected_athlete', null);
    return typeof v === 'number' ? v : null;
  },
  async setSelectedAthleteId(athleteId: number | null) {
    writeJson('selected_athlete', athleteId);
  },
  async getApiUrl() {
    const stored = readString('api_url');
    return stored || defaultApiUrl;
  },
  async setApiUrl(url: string) {
    writeString('api_url', url);
  },
  async getLocale() {
    return readString('locale', browserInitialLocale());
  },
  async setLocale(locale: string) {
    writeString('locale', locale);
  },
  async getLocaleCache(locale: string) {
    return readJson<{ version: number; content: Record<string, unknown> } | null>(
      `locale_cache_${locale}`,
      null,
    );
  },
  async setLocaleCache(locale: string, entry: { version: number; content: Record<string, unknown> }) {
    writeJson(`locale_cache_${locale}`, entry);
  },
  async getSavedComparisons() {
    return readJson<Array<Record<string, unknown>>>('saved_comparisons', []);
  },
  async saveComparison(record: Record<string, unknown>) {
    const list = await webStorage.getSavedComparisons();
    const id = String(record.id ?? '');
    const idx = list.findIndex((r) => String(r.id) === id);
    const next = idx >= 0 ? [...list] : [record, ...list];
    if (idx >= 0) next[idx] = record;
    writeJson('saved_comparisons', next.slice(0, 50));
  },
  async deleteSavedComparison(id: string) {
    const list = await webStorage.getSavedComparisons();
    writeJson(
      'saved_comparisons',
      list.filter((r) => String(r.id) !== id),
    );
  },
};
